import api from './api';
import { authFetch } from './authService';
import axios from 'axios';

// Función para obtener el token del usuario
const getUserToken = (): string | null => {
  return localStorage.getItem('token');
};

export interface Muestra {
  id?: number;
  id_m_nea: string;
  id_laboratorio?: number;
  fecha_muestreo: string;
  id_m_origen?: string;
  id_m_lab?: string;
  medio?: string;
  custodia?: string;
  georr_lat?: string;
  georr_long?: string;
  sitio_muestreo?: string;
  responsable?: string;
  pertenencia?: string;
  estado?: number;
}

// Obtener todas las muestras
export const getMuestras = async (): Promise<Muestra[]> => {
  try {
    const response = await api.get('/muestras');
    return response.data;
  } catch (error) {
    console.error('Error al obtener muestras:', error);
    return [];
  }
};

// Obtener muestras por laboratorio
export const getMuestrasByLaboratorio = async (laboratorioId: number): Promise<Muestra[]> => {
  try {
    const response = await api.get('/muestras', {
      params: { id_laboratorio: laboratorioId }
    });
    return response.data;
  } catch (error) {
    console.error(`Error al obtener muestras para laboratorio ${laboratorioId}:`, error);
    return [];
  }
};

/**
 * Genera el próximo ID-NEA basado en el ID del laboratorio
 * El formato es LAB_ID/NÚMERO_SECUENCIAL
 * @param laboratorioId ID del laboratorio seleccionado
 * @returns Promesa con el próximo ID-NEA disponible
 */
export const generarProximoIdNea = async (laboratorioId: number): Promise<string> => {
  try {
    // Simplemente generar un ID con el formato lab_id/1 sin hacer peticiones al backend
    return `${laboratorioId}/1`;
  } catch (error: any) {
    console.error('Error al generar el próximo ID-NEA:', error);
    return `${laboratorioId}/1`;
  }
};

// Crear una nueva muestra
export const createMuestra = async (muestra: Muestra): Promise<Muestra> => {
  const response = await api.post('/muestras', muestra);
  return response.data;
};

// Actualizar una muestra
export const updateMuestra = async (id: number, muestra: Partial<Muestra>): Promise<Muestra> => {
  const response = await api.put(`/muestras/${id}`, muestra);
  return response.data;
};

// Eliminar una muestra
export const deleteMuestra = async (id: number): Promise<void> => {
  await api.delete(`/muestras/${id}`);
};

// Interfaz para sitio de muestreo
export interface SitioMuestreo {
  id?: number;
  id_planta: number;
  denominacion: string;
  georreferencia_lat: number | string;
  georreferencia_long: number | string;
  utilizado: number;
}

// Interfaz para ensayo
export interface EnsayoData {
  id_muestra: number;
  id_ensayo: number;
  id_ens_control: number;
  valor: number;
  organismo: string;
  unidad: string;
  metodo_ensayo: string;
  limite_deteccion: number;
}

// Interfaz para muestra en la base de datos
export interface MuestraDBData {
  id?: number;
  id_a_nea: number;
  id_m_nea: string;
  id_m_origen: string;
  id_m_lab: string;
  id_sitio: number;
  organismo: string;
  medio: string;
  fecha_muestreo: string;
  custodia: string;
  responsable: string;
  pertenencia: string;
  observaciones: string;
  ensayos?: any[];
}

// Interfaz para análisis
export interface AnalisisData {
  id?: number;
  id_a_nea: string;
  id_a_cliente: string;
  id_a_lab: string;
  id_planta: number;
  id_origen: number;
  id_lab: number;
  estado: number;
  fecha_solicitud: string;
  fecha_carga: string;
  usuario_generador: number;
  usuario_finalizador?: number | null;
  titulo: string;
  director: string;
  analistas: string;
  firma: string;
  logo: string;
  muestras?: MuestraDBData[];
}

// Función para verificar si un sitio de muestreo ya existe
const verificarSitioExistente = async (sitio: SitioMuestreo): Promise<number | null> => {
  try {
    const response = await api.post('/sitios-muestreo/verificar', sitio);
    
    if (response.data && response.data.id) {
      return response.data.id;
    }
    return null;
  } catch (error) {
    console.error('Error al verificar sitio de muestreo:', error);
    return null;
  }
};

// Función para crear un nuevo sitio de muestreo
const crearSitioMuestreo = async (sitio: SitioMuestreo): Promise<number> => {
  try {
    const response = await api.post('/sitios-muestreo', sitio);
    
    return response.data.id;
  } catch (error) {
    console.error('Error al crear sitio de muestreo:', error);
    // En caso de error, devolver un ID ficticio para no bloquear el flujo
    return -1;
  }
};

// Función para guardar análisis completo
export const guardarAnalisisCompleto = async (analisisData: any) => {
  try {
    // 1. Preparar datos para el análisis
    const fechaActual = new Date().toISOString();
    
    const analisis: AnalisisData = {
      id_a_nea: 'NEA_TEMP', // Valor temporal, se actualizará después de crear el análisis
      id_a_cliente: analisisData.mostrar_numero_origen ? analisisData.numero_analisis_origen : '',
      id_a_lab: analisisData.mostrar_numero_laboratorio ? analisisData.numero_analisis_laboratorio : '',
      id_planta: analisisData.planta ? parseInt(analisisData.planta) : -1,
      id_origen: parseInt(analisisData.id_cliente.toString()),
      id_lab: parseInt(analisisData.id_laboratorio.toString()),
      estado: 0, // Siempre 0 (pendiente) al crear
      fecha_solicitud: fechaActual,
      fecha_carga: '', // Vacío al crear
      usuario_generador: parseInt(analisisData.usuario_id || '0'),
      titulo: '', // Vacío al crear
      director: '', // Vacío al crear
      analistas: '', // Vacío al crear
      firma: '', // Vacío al crear
      logo: '', // Vacío al crear
    };
    
    // 2. Guardar el análisis
    const responseAnalisis = await api.post('/analisis', analisis);
    
    const analisisId = responseAnalisis.data.id;
    
    // 3. Actualizar el id_a_nea con el ID real del análisis
    const idNeaFinal = `NEA/${analisisId}`;
    await api.put(`/analisis/${analisisId}`, {
      id_a_nea: idNeaFinal
    });
    
    // 4. Procesar las muestras y sus ensayos
    const muestrasPromises = analisisData.muestras.map(async (muestra: any, index: number) => {
      let sitioId = -1;
      
      try {
        // Crear un nuevo sitio directamente sin verificar si ya existe
        const sitioData = {
          id_planta: analisisData.planta ? parseInt(analisisData.planta) : -1,
          denominacion: muestra.sitio_muestreo,
          georreferencia_lat: parseFloat(muestra.georr_lat) || 0,
          georreferencia_long: parseFloat(muestra.georr_long) || 0,
          utilizado: 1 // Marcar como utilizado
        };
        
        // Intentar crear el sitio, si falla usamos un ID temporal
        sitioId = await crearSitioMuestreo(sitioData);
      } catch (error) {
        console.error('Error al crear sitio, usando ID temporal:', error);
        sitioId = -index - 1; // ID temporal negativo único
      }
      
      // Preparar datos de la muestra con formato de fecha correcto
      // Usar formato ISO-8601 completo para las fechas
      const fechaMuestreo = muestra.fecha_muestreo 
        ? new Date(muestra.fecha_muestreo).toISOString() 
        : fechaActual;
      
      console.log('Fecha muestreo:', fechaMuestreo);
      
      const muestraDB: MuestraDBData = {
        id_a_nea: analisisId,
        id_m_nea: muestra.id_nea || `${analisisData.id_laboratorio}/1-${index+1}`,
        id_m_origen: muestra.id_m_origen || '',
        id_m_lab: muestra.id_m_lab || '',
        id_sitio: sitioId,
        organismo: muestra.mostrar_organismo_control && muestra.organismo_control_id ? muestra.organismo_control_id.toString() : '',
        medio: muestra.medio || '',
        fecha_muestreo: fechaMuestreo,
        custodia: muestra.mostrar_cadena_custodia ? muestra.cadena_custodia || '' : '',
        responsable: muestra.responsable || '',
        pertenencia: muestra.pertenencia || '',
        observaciones: muestra.descripcion || ''
      };
      
      // Imprimir los datos enviados para depuración
      console.log('Datos de la muestra a enviar:', JSON.stringify(muestraDB));
      
      // Guardar la muestra
      const responseMuestra = await api.post('/muestras', muestraDB);
      
      const muestraId = responseMuestra.data.id;
      
      // Intentar guardar los ensayos de esta muestra, pero no bloquear el proceso si fallan
      try {
        // Almacenar todos los ensayos para un posible uso posterior
        const ensayosData: Array<any> = [];
        
        if (muestra.ensayos && muestra.ensayos.length > 0) {
          for (const ensayo of muestra.ensayos) {
            if (!ensayo.id) continue; // Ignorar ensayos sin ID
            
            // Imprimir el ensayo para depuración
            console.log('Datos del ensayo a enviar:', ensayo);
            
            // Crear el registro de ensayo según la estructura explicada
            const ensayoRecord: any = {
              id_muestra: muestraId,
              id_ensayo: parseInt(ensayo.id.toString()),
              id_ens_control: 0, // Valor por defecto
              valor: parseFloat(ensayo.resultado) || 0,
              organismo: muestra.mostrar_organismo_control && muestra.organismo_control_id ? muestra.organismo_control_id.toString() : '',
              unidad: ensayo.unidad || '',
              metodo_ensayo: '', // Dejar vacío como indicó el usuario
              limite_deteccion: 0, // Dejar en 0 como indicó el usuario
              saved: false // Flag para controlar si se guardó con éxito
            };
            
            console.log('Datos de ensayo a guardar:', ensayoRecord);
            
            // Guardar para un posible uso posterior
            ensayosData.push(ensayoRecord);
            
            // Intentar guardar usando el nuevo endpoint específico para ensayo-muestra
            try {
              const response = await api.post('/ensayos/muestra', ensayoRecord);
              console.log('Ensayo guardado exitosamente:', response.data);
              ensayoRecord.saved = true; // Marcar como guardado
            } catch (err) {
              console.warn('Error al guardar ensayo-muestra:', err);
              
              // Si falla, intentamos las alternativas anteriores
              try {
                const altResponse = await api.post('/ensayos', {
                  ...ensayoRecord,
                  // Además de los campos para la tabla 'ensayo', 
                  // proporcionamos los campos requeridos por el controlador
                  nombre: ensayo.analito || 'Ensayo sin nombre',
                  metodo: 'Método estándar',
                  codigo: `ENS-${ensayo.id}`,
                  descripcion: ensayo.analito || 'Ensayo',
                  laboratorio_id: analisisData.id_laboratorio
                }).catch(e => {
                  console.warn('Error al intentar segunda alternativa:', e);
                  return null;
                });
                
                if (altResponse) {
                  ensayoRecord.saved = true; // Marcar como guardado si la alternativa tuvo éxito
                }
              } catch (e2) {
                console.warn('Error en segunda alternativa:', e2);
              }
            }
          }
          
          // Si tenemos ensayos para guardar y los intentos individuales fallaron,
          // intentamos una última opción en bloque
          if (ensayosData.length > 0 && ensayosData.some(e => !e.saved)) {
            try {
              console.log('Intentando guardar ensayos en bloque...');
              
              // Intentar guardar todos los ensayos de una vez
              const response = await api.post('/analisis/ensayos', {
                id_analisis: analisisId,
                ensayos: ensayosData.filter(e => !e.saved)
              }).catch(err => {
                console.warn('Error al guardar ensayos en bloque (no crítico):', err);
                return null;
              });
              
              if (response && response.data) {
                console.log('Ensayos guardados en bloque:', response.data);
                // Marcar todos como guardados
                ensayosData.forEach(e => {
                  if (!e.saved) e.saved = true;
                });
              }
            } catch (bulkError) {
              console.warn('Error al guardar ensayos en bloque (no crítico):', bulkError);
            }
          }
        }
      } catch (ensayosError) {
        console.warn('Error general al procesar ensayos (no crítico):', ensayosError);
        // No interrumpir el proceso principal por error en ensayos
      }
      
      return muestraId;
    });
    
    await Promise.all(muestrasPromises);
    
    // La operación se considera exitosa incluso si hubo problemas con los ensayos
    return { success: true, analisisId: responseAnalisis.data.id };
    
  } catch (error) {
    console.error('Error al guardar el análisis completo:', error);
    throw error;
  }
};

// Interfaz para muestra con datos completos para el componente CrearAnalisis
export interface MuestraData {
  id: number;
  id_nea: string;
  id_m_origen: string;
  id_m_lab: string;
  id_muestra_lab: string;
  responsable: string;
  pertenencia: string;
  sitio_muestreo: string;
  georr_lat: string;
  georr_long: string;
  medio: string;
  fecha_muestreo: string;
  ensayos: any[]; // Definición genérica para ensayos
  mostrar_organismo_control: boolean;
  organismo_control_id: number | null;
  mostrar_cadena_custodia: boolean;
  cadena_custodia: string;
  remito?: string;
  descripcion?: string;
} 